import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

interface PasswordStrengthIndicatorProps {
  password: string;
  transparent?: boolean;
  compact?: boolean;
}

interface PasswordCriteria {
  label: string;
  met: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  transparent = false,
  compact = false,
}) => {
  const isCompact = compact;
  // Calculate password criteria
  const criteria: PasswordCriteria[] = [
    {
      label: '8+',
      met: password.length >= 8
    },
    {
      label: 'A-Z',
      met: /[A-Z]/.test(password)
    },
    {
      label: 'a-z',
      met: /[a-z]/.test(password)
    },
    {
      label: '0-9',
      met: /[0-9]/.test(password)
    },
    {
      label: '!@#',
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    }
  ];

  // Calculate strength score (0-5)
  const strengthScore = criteria.filter(c => c.met).length;
  
  // Calculate strength percentage
  const strengthPercentage = (strengthScore / 5) * 100;
  
  // Determine strength level and color
  const getStrengthLevel = () => {
    if (strengthScore === 0) return { level: '', color: '#E0E0E0' };
    if (strengthScore === 1) return { level: 'Weak', color: '#FF5722' };
    if (strengthScore === 2) return { level: 'Fair', color: '#FF9800' };
    if (strengthScore === 3) return { level: 'Good', color: '#FF9800' };
    if (strengthScore === 4) return { level: 'Strong', color: '#4CAF50' };
    if (strengthScore === 5) return { level: 'Very Strong', color: '#4CAF50' };
    return { level: '', color: '#E0E0E0' };
  };

  const { level, color } = getStrengthLevel();

  return (
    <Box
      sx={{
        backgroundColor: transparent ? 'transparent' : '#F5F5F5',
        borderRadius: '8px',
        padding: transparent ? '2px 4px' : { xs: '8px 12px', sm: '12px 16px' },
        marginTop: '8px',
        display: 'flex',
        flexDirection:  { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: isCompact ? 0.75 : { xs: 1, sm: 0 },
        minHeight: transparent ? 0 : { xs: 'auto', sm: '40px' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isCompact ? '8px' : { xs: '10px', sm: '16px' },
          alignItems: 'center',
          minWidth: 0
        }}
      >
        {criteria.map((criterion, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '4px', sm: '6px' }
            }}
          >
            <Box
              sx={{
                width: isCompact ? '12px' : { xs: '14px', sm: '16px' },
                height: isCompact ? '12px' : { xs: '14px', sm: '16px' },
                borderRadius: '50%',
                backgroundColor: criterion.met ? '#4CAF50' : '#FFFFFF',
                border: '2px solid',
                borderColor: criterion.met ? '#4CAF50' : '#E0E0E0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isCompact ? '8px' : { xs: '9px', sm: '10px' },
                color: '#FFFFFF',
                fontWeight: 'bold',
                flexShrink: 0
              }}
            >
              {criterion.met && '✓'}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: isCompact ? '10px' : { xs: '11px', sm: '12px' },
                fontWeight: '500',
                color: '#333333',
                whiteSpace: 'nowrap'
              }}
            >
              {criterion.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isCompact ? '6px' : '8px',
          minWidth: isCompact ? '112px' : { xs: '100%', sm: '120px' },
          flexShrink: 0
        }}
      >
        <Box sx={{ flex: 1, minWidth: isCompact ? '54px' : { xs: 0, sm: '60px' } }}>
          <LinearProgress
            variant="determinate"
            value={strengthPercentage}
            sx={{
              height: isCompact ? '5px' : '6px',
              borderRadius: '3px',
              backgroundColor: '#E0E0E0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: '3px'
              }
            }}
          />
        </Box>
        {level && (
          <Typography
            variant="body2"
            sx={{
              fontSize: isCompact ? '10px' : { xs: '11px', sm: '12px' },
              fontWeight: '600',
              color: color,
              minWidth: isCompact ? 'fit-content' : '40px',
              whiteSpace: 'nowrap'
            }}
          >
            {level}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
